import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getDevices, deleteDevice, updateDevice, Device } from '../services/api'

const STATUS_FILTERS = [
  { value: '', label: '全部' },
  { value: 'online', label: '在线' },
  { value: 'offline', label: '离线' },
  { value: 'connecting', label: '连接中' },
  { value: 'error', label: '错误' },
]

// 设备 ID 形如 android-<applicationId>-<ANDROID_ID>，从中解析出安装的 APK。
const APP_NAME_BY_PACKAGE: Record<string, string> = {
  'com.eden.garden': 'admin-app-chengren',
  'com.eden.gardena': 'android-app-yingshi',
  'com.eden.gardenb': 'auto-app-liuhecan',
  'com.eden.demo': 'demo-app',
  'com.eden.field': 'field-app',
  'com.eden.kiosk': 'kiosk-app',
  'com.eden.partner': 'partner-app',
  'com.eden.test': 'test-app',
  'com.eden.tv': 'tv-app',
  'com.eden.wear': 'wear-app',
}

function getAppName(deviceId: string) {
  const m = /^android-(.+)-[0-9a-f]{6,}$/i.exec(deviceId || '')
  const pkg = m ? m[1] : ''
  return APP_NAME_BY_PACKAGE[pkg] || pkg || '未知应用'
}

const STATUS_TEXT: Record<string, string> = {
  online: '在线',
  offline: '离线',
  connecting: '连接中',
  disconnected: '已断开',
  error: '错误',
}

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [editing, setEditing] = useState<Device | null>(null)
  const [editForm, setEditForm] = useState({ model: '', manufacturer: '', android_version: '' })
  const [error, setError] = useState('')

  const fetchDevices = useCallback(async (p = page, search = '', status = '', silent = false) => {
    if (!silent) setLoading(true)
    setError('')
    try {
      // 状态与关键字筛选交给后端，分页和「共 N 台设备」才会与筛选结果一致
      const response = await getDevices(p, 20, status || undefined, search || undefined)
      setDevices(response.data)
      setTotal(response.pagination?.total ?? response.data.length)
      setTotalPages(response.pagination?.totalPages ?? 1)
    } catch (e) {
      setError('加载设备失败，请检查网络或登录状态')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [page])

  useEffect(() => {
    fetchDevices(page, search, statusFilter)
  }, [page, search, statusFilter, fetchDevices])

  useEffect(() => {
    const timer = setInterval(() => fetchDevices(page, search, statusFilter, true), 10000)
    return () => clearInterval(timer)
  }, [page, search, statusFilter, fetchDevices])

  const handleDelete = async (deviceId: string) => {
    if (!window.confirm('确定要删除该设备吗？此操作不可恢复。')) return
    try {
      await deleteDevice(deviceId)
      fetchDevices(page, search, statusFilter)
    } catch (e) {
      window.alert('删除设备失败')
    }
  }

  const openEdit = (device: Device) => {
    setEditing(device)
    setEditForm({
      model: device.model || '',
      manufacturer: device.manufacturer || '',
      android_version: device.android_version || '',
    })
  }

  const saveEdit = async () => {
    if (!editing) return
    try {
      await updateDevice(editing.device_id, {
        model: editForm.model,
        manufacturer: editForm.manufacturer,
        android_version: editForm.android_version,
      })
      setEditing(null)
      fetchDevices(page, search, statusFilter)
    } catch (e) {
      window.alert(e instanceof Error ? e.message : '保存失败')
    }
  }

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      online: 'bg-green-100 text-green-800',
      offline: 'bg-gray-100 text-gray-800',
      connecting: 'bg-blue-100 text-blue-800',
      disconnected: 'bg-gray-100 text-gray-800',
      error: 'bg-red-100 text-red-800',
    }
    const style = map[status] || 'bg-gray-100 text-gray-800'
    return <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${style}`}>{STATUS_TEXT[status] || status || '未知'}</span>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">设备管理</h1>
          <p className="text-gray-600">共 {total} 台设备</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="搜索设备 ID / 型号 / 厂商..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <div className="flex items-center gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(1) }}
              className={`px-3 py-1.5 text-sm rounded-md border ${
                statusFilter === f.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">应用</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">型号</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">厂商</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Android</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">电量</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最后在线</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">加载中...</td></tr>
            ) : devices.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center text-gray-500">{search || statusFilter ? '未找到匹配的设备' : '暂无设备，等待设备注册'}</td></tr>
            ) : (
              devices.map((device) => (
                <tr key={device.device_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link to={`/devices/${device.device_id}`} className="text-blue-600 hover:text-blue-900 font-mono text-sm" title={device.device_id}>
                      {getAppName(device.device_id)}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{device.model}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.manufacturer}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.android_version}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{statusBadge(device.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{device.battery_level}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(device.last_seen).toLocaleString('zh-CN')}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link to={`/devices/${device.device_id}`} className="text-indigo-600 hover:text-indigo-900 mr-3">详情</Link>
                    <button onClick={() => openEdit(device)} className="text-indigo-600 hover:text-indigo-900 mr-3">编辑</button>
                    <button onClick={() => handleDelete(device.device_id)} className="text-red-600 hover:text-red-900">删除</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">第 {page} 页，共 {totalPages} 页</div>
            <div className="flex space-x-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50">上一页</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50">下一页</button>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">编辑设备</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">型号</label>
                <input type="text" value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">厂商</label>
                <input type="text" value={editForm.manufacturer} onChange={(e) => setEditForm({ ...editForm, manufacturer: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Android 版本</label>
                <input type="text" value={editForm.android_version} onChange={(e) => setEditForm({ ...editForm, android_version: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">取消</button>
                <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
